import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { TooltipTableHeadComponent } from "../../common/TableHead";
import { MenuItem, Switch, TextField } from "@mui/material";
import {
  buildStreamParameterTooltips,
  choiceValue,
  isCheckboxStreamParameter,
  isChoiceStreamParameter,
  isPerCameraProtocolField,
  relabelProtocolChoiceOptions,
  StreamParameterOption,
} from "./EditCameraUtils/streamParameterFields";
import { Dispatcher } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";

const buildFieldValuesFromData = (
  extraFields: { field: string }[],
  streamParametersData: Record<string, unknown>
): Record<string, unknown> => {
  const values: Record<string, unknown> = {};
  extraFields.forEach((field) => {
    values[field.field] = streamParametersData[field.field];
  });
  return values;
};

const choiceLabelForValue = (
  options: StreamParameterOption[],
  value: unknown
): string => {
  const normalized = choiceValue(value);
  return (
    options.find(([, optionValue]) => optionValue === normalized)?.[0] ??
    normalized
  );
};

export const StreamParametersTabEditMode = ({
  streamParametersData,
  streamParametersPatchObject: _streamParametersPatchObject,
  setStreamParametersPatchObject,
  setStreamParametersChangesMade,
  siteInfo,
}: {
  streamParametersData: any;
  streamParametersPatchObject: any;
  setStreamParametersPatchObject: any;
  setStreamParametersChangesMade: Dispatcher<boolean>;
  siteInfo?: { protocol?: string | null };
}) => {
  const extraFields = streamParametersData.extra_fields;

  // Edit Camera stores the active tab JSX in state, so props like
  // streamParametersPatchObject are frozen at tab-switch time. Keep pending
  // extra-field edits in local state for a responsive UI; still mirror into
  // the parent patch object for Save.
  const [fieldValues, setFieldValues] = React.useState<Record<string, unknown>>(
    () => buildFieldValuesFromData(extraFields, streamParametersData)
  );

  const commitFieldValue = (field: string, nextValue: unknown) => {
    setFieldValues((previous) => ({ ...previous, [field]: nextValue }));
    setStreamParametersChangesMade(true);
    setStreamParametersPatchObject((previous: any) => ({
      ...previous,
      [field]: nextValue,
    }));
  };

  // The integration writes a helperText for each field (actuate_admin's
  // list_extra_camera_fields); surface it on the header rather than dropping it.
  const titlesArray = extraFields.map((field: any) => [
    field.label,
    field.field,
  ]);
  const toolTipText = buildStreamParameterTooltips(extraFields);

  const didSeedPatch = React.useRef(false);
  React.useEffect(() => {
    if (didSeedPatch.current) {
      return;
    }
    didSeedPatch.current = true;
    setStreamParametersPatchObject(
      buildFieldValuesFromData(extraFields, streamParametersData)
    );
  }, [extraFields, setStreamParametersPatchObject, streamParametersData]);

  void _streamParametersPatchObject;

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 1000, fontFamily: "Mulish" }}>
        <TooltipTableHeadComponent
          titlesArray={titlesArray}
          toolTipText={toolTipText}
        />
        <TableBody>
          <TableRow key={`stream_parameters`}>
            {extraFields.map((object: any) => (
              <TableCell
                align="left"
                key={object.id}
                sx={{ fontFamily: "Mulish" }}
              >
                <div>
                  {isCheckboxStreamParameter(object.type) ? (
                    <Switch
                      id={object.id}
                      inputProps={{ "aria-label": object.label }}
                      checked={Boolean(fieldValues[object.field])}
                      onChange={() => {
                        commitFieldValue(
                          object.field,
                          !Boolean(fieldValues[object.field])
                        );
                      }}
                    />
                  ) : isChoiceStreamParameter(object.type) ? (
                    (() => {
                      const choiceOptions = isPerCameraProtocolField(
                        object.field,
                        object.label
                      )
                        ? relabelProtocolChoiceOptions(
                            object.options,
                            siteInfo?.protocol
                          )
                        : object.options || [];
                      const selectedValue = choiceValue(
                        fieldValues[object.field]
                      );
                      return (
                        <TextField
                          id={object.id}
                          select
                          variant="standard"
                          sx={{ minWidth: 280 }}
                          inputProps={{ "aria-label": object.label }}
                          value={selectedValue}
                          SelectProps={
                            isPerCameraProtocolField(
                              object.field,
                              object.label
                            )
                              ? {
                                  displayEmpty: true,
                                  renderValue: (selected: unknown) =>
                                    choiceLabelForValue(
                                      choiceOptions,
                                      selected
                                    ),
                                }
                              : undefined
                          }
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            commitFieldValue(object.field, nextValue);
                          }}
                          helperText={
                            object.helperText ? object.helperText : ""
                          }
                        >
                          {choiceOptions.map(
                            ([label, value]: StreamParameterOption) => (
                              <MenuItem key={value || "default"} value={value}>
                                {label}
                              </MenuItem>
                            )
                          )}
                        </TextField>
                      );
                    })()
                  ) : (
                    <TextField
                      id={object.id}
                      value={
                        fieldValues[object.field] !== null &&
                        fieldValues[object.field] !== undefined
                          ? String(fieldValues[object.field])
                          : ""
                      }
                      variant="standard"
                      sx={{ width: 150 }}
                      type="string"
                      onChange={(event) => {
                        commitFieldValue(object.field, event.target.value);
                      }}
                      helperText={object.helperText ? object.helperText : ""}
                    />
                  )}
                </div>
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};
